/**
Copyright (C) 2019-2025, Infraclear Inc. All Rights Reserved.
*/

import { Button, TextField, MenuItem, Select, ListItemText, FormControl, FormHelperText, RadioGroup, FormControlLabel, Radio, ListSubheader, Box } from "@mui/material";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import {
  ErrorMessage,
  Field,
  Form,
  Formik,
} from "formik";
import React, { useEffect, useRef, useState } from "react";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { Add } from '@mui/icons-material';
import * as Yup from "yup";
import { MessageText } from "../../constants/messageText";
import {
  FetchTeams,
  GetUserRoles,
  MultipleUserEnrolment,
} from "../../constants/api-urlConstants";
import instance from "../../utils/axiosInstance";
import { ErrorCodes } from "../../constants/messageCodes";
import { SnackBarMessage } from "../snackBar/snackBar";
import CircularProgress from "@mui/material/CircularProgress";
import "./userEnrolmentForm.scss";
import { StyledTableCellFooterRow, StyledTableCellHeadRow, StyledTableCellMailRow, StyledTableCellRow } from "./userEnrolmentFormCommanStyle";
import { alphaNumaricInput, numaricInput, ROLES } from "../../constants/commonConstants";
import { TimerValusSet } from "../../context/TimerContext";
import { StyledTableCell, StyledTableCellRole, StyledTableCellTrialDays } from "../../pages/userEnrolment/userEnrolmentCommanStyle";
import CreateTeam from "../team";
import { Modal } from "../Modal";
import { isBasicOrProUser, isDataBrowserUser, isPayGUserRoleSelected } from "../../utils";
import { Role } from "../rolesAssign/model";
import { DISABLE_ROLES } from "../../utils/config";

export const UserEnrolmentForm: React.FC<any> = (props) => {
  const { setUserHeartBeat, timeExit } = React.useContext(TimerValusSet);
  const [newFormAdd, setNewFormAdd] = React.useState([
    { form: `div`, isFilled: false, isEmailExist: false, selectedRoles: [], datePickerOpen: false },
  ]);
  const [teams, setTeams] = useState([]);
  const ref = useRef(null);
  const [isLoading, setISLoading] = React.useState(false);
  const [openCreateNewTeam, setOpenCreateNewTeam] = React.useState(false);
  const [rolesList, setRoles] = React.useState<any>([]);
  const [msgConfig, setMsgConfig] = React.useState({
    type: "",
    message: "",
    snackFlag: false,
  });
  const initial_state = {
    id: 1,
    company: "",
    firstName: "",
    lastName: "",
    email: "",
    refinitivLoginId: "",
    trialDays: "",
    roles: [],
    team: "",
    isMfaEnabled: false,
    isTwofaEnabled: true,
    expirationDate: null,
  };

  useEffect(() => {
    getRoles();
    fetchTeamsList();
  }, []);

  const AddNewRow = () => {
    const current = [...newFormAdd];
    current.push({ form: `div`, isFilled: false, isEmailExist: false, selectedRoles: [], datePickerOpen: false });
    if (newFormAdd.length === 1) {
      props.backButtonChange(true);
    }
    setNewFormAdd(current);
  };

  const onlyText = /^[A-Za-z0-9 ]+$/;
  const noFreeMailBoxes = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9_.+-]+\.[a-zA-Z]{2,6}$/gm;

  const UserEnrolmentFormValidation = Yup.object().shape({
    company: Yup.string()
      .min(2, MessageText.VALIDATION_MIN_2)
      .trim(MessageText.VALIDATION_COMPANY_REQUIRED)
      .required(MessageText.VALIDATION_COMPANY_REQUIRED),
    firstName: Yup.string()
      .min(2, MessageText.VALIDATION_MIN_2)
      .trim(MessageText.VALIDATION_FIRSTNAME_REQUIRED)
      .required(MessageText.VALIDATION_FIRSTNAME_REQUIRED)
      .matches(onlyText, MessageText.SPECIAL_CHARACTERS_ARE_NOT_ALLOWED),
    lastName: Yup.string()
      .trim(MessageText.VALIDATION_LASTNAME_REQUIRED)
      .required(MessageText.VALIDATION_LASTNAME_REQUIRED)
      .matches(onlyText, MessageText.SPECIAL_CHARACTERS_ARE_NOT_ALLOWED),
    email: Yup.string()
      .email(MessageText.VALIDATION_EMAIL_FORMAT)
      .matches(noFreeMailBoxes, MessageText.VALID_EMAIL_ADDRESS)
      .required(MessageText.EMAIL_REQUIRED),
    trialDays: Yup
      .number()
      .integer()
      .moreThan(1, MessageText.TRIALDAYS_PERIOD)
      .lessThan(101, MessageText.TRIALDAYS_PERIOD),
    roles: Yup
      .array().of(Yup.number())
      .min(1, MessageText.VALIDATION_ROLE_REQUIRED),
    team: Yup
      .string()
      .trim(MessageText.VALIDATION_TEAM_REQUIRED)
      .when("roles", ([roles], schema) => {
        const selectedRoles = rolesList.filter((element: Role) => roles?.includes(element.id)).map((e: any) => e?.name?.key);
        if (isPayGUserRoleSelected(selectedRoles) || isBasicOrProUser(selectedRoles) || isDataBrowserUser(selectedRoles)) {
          return schema.required(MessageText.VALIDATION_TEAM_REQUIRED);
        }
        return schema.notRequired();
      }),
    expirationDate: Yup.date()
      .nullable()
      .when("roles", ([roles], schema) => {
        const selectedRoles = rolesList
          .filter((element: Role) => roles?.includes(element.id))
          .map((e: any) => e?.name?.key);
        if (selectedRoles.includes(ROLES.CONTRACTS_SUBSCRIBER)) {
          return schema
            .required("Expiration date is required")
            .min(dayjs().startOf('day').toDate(),"Expiration date cannot be in the past");
        }
        return schema.notRequired();
      }),
  });

  const createUser = async (creds: any, currentItem: any) => {
    setISLoading(true);
    currentItem.isEmailExist = false;
    setMsgConfig({
      type: "error",
      message: MessageText.WENT_WRONG,
      snackFlag: false,
    });
    const USER_API_BASE_URL = `${MultipleUserEnrolment}`;
    const payload: any = {
      firstName: creds.firstName.trim(),
      lastName: creds.lastName.trim(),
      email: creds.email,
      trialDays: creds.trialDays,
      company: creds.company.trim(),
      refinitivLoginId: creds.refinitivLoginId,
      roles: creds.roles,
      isMfaEnabled: creds?.isMfaEnabled,
      isTwofaEnabled: creds?.isTwofaEnabled,
      expirationDate: creds.expirationDate ? dayjs(creds.expirationDate).format("YYYY-MM-DD") : '',
    };
    const selectedRoles = rolesList.filter((element: Role) => creds.roles.includes(element.id)).map((e: any) => e?.name?.key);
    if (isPayGUserRoleSelected(selectedRoles) || isBasicOrProUser(selectedRoles) || isDataBrowserUser(selectedRoles)) {
      payload.teamId = creds?.team;
    }

    instance
      .post(USER_API_BASE_URL, payload)
      .then((response) => {
        setUserHeartBeat(timeExit());
        setISLoading(false);
        const res = response.data;
        if (res.success) {
          currentItem.isFilled = true;
          AddNewRow();
        } else if (res.message_code === ErrorCodes.DUPLICATE_USERNAME) {
          currentItem.isEmailExist = true;
        } else if (res.message_code === ErrorCodes.INVALID_EMAIL) {
          setMsgConfig({
            type: "error",
            message: MessageText.EMAIL_FORMAT_INVALID,
            snackFlag: true,
          });
        } else {
          setMsgConfig({
            type: "error",
            message: MessageText.WENT_WRONG,
            snackFlag: true,
          });
        }
      })
      .catch(() => { });
  };

  const getRoles = () => {
    const USER_API_BASE_URL = `${GetUserRoles}`;
    instance
      .get(USER_API_BASE_URL)
      .then((response) => {
        const res = response.data;
        setUserHeartBeat(timeExit());
        if (res.success) {
          res.data.forEach((item: any) => {
            item.isDisabled = false;
          });
          setRoles(res.data);
        }
      })
      .catch(() => { });
  }

  const handleAuthentication = (formik: any) => {
    formik?.setFieldValue('isMfaEnabled', false);
    formik?.setFieldValue('isTwofaEnabled', false);
  }

  const handleAuth = (formik: any) => {
    formik?.setFieldValue('isTwofaEnabled', true);
  }

  const fetchTeamsList = () => {
    const TEAMS_API_BASE_URL = `${FetchTeams}`;
    instance.get(TEAMS_API_BASE_URL).then((res) => {
      const teams = res.data.data;
      setTeams(teams);
    });
  }

  const handleCloseCreateNewTeam = () => {
    setOpenCreateNewTeam(false);
  }

  const fetchAndClose = () => {
    fetchTeamsList();
    handleCloseCreateNewTeam();
  }

  const handleResetAllField = (formik: any) => {
    formik?.setFieldValue('expirationDate', initial_state.expirationDate);
    formik?.setFieldValue('team', initial_state.team);
    formik?.setFieldValue('trialDays', initial_state.trialDays);
  };

  const setDatePickerOpen = (index: number, value: boolean) => {
    const updated = [...newFormAdd];
    updated[index].datePickerOpen = value;
    setNewFormAdd(updated);
  };

  return (
    <>
      {newFormAdd.map((currentItem, index) => {
        return (
          <div key={index} id={`expense-${index}`} className="mainTableRows">
            <Formik
              innerRef={ref}
              initialValues={initial_state}
              validationSchema={UserEnrolmentFormValidation}
              onSubmit={(values: any) => {
                createUser(values, currentItem);
              }}
            >
              {(formik: any) => {
                const selectedRoles = rolesList.filter((element: Role) => formik.values.roles.includes(element.id)).map((e: any) => e?.name?.key);
                return (
                  <React.Fragment>
                    <Form className="formFormik">
                      <StyledTableCellHeadRow className={!(formik.isValid && formik.dirty) ? "tebleCellButtonCount" : "tableInvalidCount"} ><div className="idPara"><span>{index + 1}</span></div></StyledTableCellHeadRow>
                      <StyledTableCellRow className="tebleCell">
                        <Field
                          className="nameParameter"
                          as={TextField}
                          fullWidth
                          size="small"
                          id="company"
                          name="company"
                          autoComplete="off"
                          disabled={currentItem.isFilled}
                          error={
                            formik.touched.company &&
                            Boolean(formik.errors.company)
                          }
                          helperText={<ErrorMessage name="company" />}
                        />
                      </StyledTableCellRow>
                      <StyledTableCellRow className="tebleCell">
                        <Field
                          className="nameParameter"
                          as={TextField}
                          size="small"
                          id="firstName"
                          name="firstName"
                          autoComplete="off"
                          inputProps={{
                            maxLength: 25,
                          }}
                          onKeyPress={(e: any) => alphaNumaricInput(e)}
                          disabled={currentItem.isFilled}
                          error={
                            formik.touched.firstName &&
                            Boolean(formik.errors.firstName)
                          }
                          helperText={<ErrorMessage name="firstName" />}
                        />
                      </StyledTableCellRow>
                      <StyledTableCellRow className="tebleCell">
                        <Field
                          className="nameParameter"
                          as={TextField}
                          size="small"
                          id="lastName"
                          name="lastName"
                          autoComplete="off"
                          inputProps={{
                            maxLength: 25,
                          }}
                          onKeyPress={(e: any) => alphaNumaricInput(e)}
                          disabled={currentItem.isFilled}
                          error={
                            formik.touched.lastName &&
                            Boolean(formik.errors.lastName)
                          }
                          helperText={<ErrorMessage name="lastName" />}
                        />
                      </StyledTableCellRow>
                      <StyledTableCellMailRow className="tebleCell">
                        <Field
                          as={TextField}
                          className="emailParameter"
                          fullWidth
                          size="small"
                          id="email"
                          name="email"
                          autoComplete="off"
                          disabled={currentItem.isFilled}
                          onChange={(e: any) => {
                            if (currentItem.isEmailExist) {
                              currentItem.isEmailExist = false;
                            }
                            formik.handleChange(e);
                          }}
                          error={
                            (formik.touched.email &&
                              Boolean(formik.errors.email)) ||
                            currentItem.isEmailExist
                          }
                          helperText={
                            currentItem.isEmailExist ? (
                              MessageText.EMAIL_ALREADY_EXIST
                            ) : (
                              <ErrorMessage name="email" />
                            )
                          }
                        />
                      </StyledTableCellMailRow>

                      <StyledTableCellRole className="tebleCell">
                        <FormControl sx={{ m: 1, width: '92%', margin: 0 }} error={formik.touched.roles && !formik.values.roles.length}>
                          <Select
                            labelId="demo-multiple-checkbox-label"
                            id="demo-multiple-checkbox"
                            className="user-role"
                            name="roles"
                            value={formik.values.roles[0] ?? ''}
                            onChange={e => {
                              if (formik.values.roles[0] !== e.target.value) {
                                handleResetAllField(formik);
                              }
                              const roles = [e.target.value];
                              const selectedRoles = rolesList.filter((element: Role) => roles.includes(element.id)).map((e: any) => e?.name?.key);
                              if (DISABLE_ROLES.includes(selectedRoles?.[0])) {
                                formik.setFieldValue('trialDays', '', false);
                                formik.setFieldTouched('trialDays', false);
                                formik.setErrors((prevErrors: any) => ({
                                  ...prevErrors,
                                  trialDays: '',
                                }));
                              }
                              (selectedRoles.includes(ROLES.CONTRACTS_TRAIL) || selectedRoles.includes(ROLES.GUEST) || selectedRoles.includes(ROLES.SSO_USER) || selectedRoles.includes(ROLES.BULKDATA_API_USER)) ? handleAuthentication(formik)
                                : handleAuth(formik);
                              formik.setFieldValue('roles', roles);
                              setTimeout(() => {
                                formik.validateForm();
                              }, 10);
                              const existingList = JSON.parse(JSON.stringify(newFormAdd));
                              existingList[index].selectedRoles = selectedRoles;
                              setNewFormAdd(existingList);
                              props?.callback(isPayGUserRoleSelected(selectedRoles) || isBasicOrProUser(selectedRoles) || isDataBrowserUser(selectedRoles));
                            }}
                            onClick={() => {
                              formik.setFieldTouched('roles')
                            }}
                            renderValue={value => {
                              let roleIds: any = [];
                              rolesList.forEach((role: any) => {
                                if (value === role.id) {
                                  roleIds.push(role.name.value)
                                }
                              });
                              return roleIds.join(', ')
                            }}
                            disabled={currentItem.isFilled}
                          >
                            {rolesList.length && rolesList.map((role: any) => (
                              <MenuItem key={role.id} value={role.id} disabled={role.isDisabled}>
                                <ListItemText primary={role.name.value} />
                              </MenuItem>
                            ))}
                          </Select>
                          {formik.touched.roles && !formik.values.roles.length &&
                            <FormHelperText>
                              {formik.errors.roles}
                            </FormHelperText>
                          }
                        </FormControl>
                      </StyledTableCellRole>
                      <StyledTableCellTrialDays className="tebleCell">
                        {selectedRoles.includes(ROLES.CONTRACTS_SUBSCRIBER)
                          ? (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                className="cs-date-picker"
                                disabled={currentItem.isFilled}
                                value={formik.values.expirationDate}
                                onChange={(value) => formik.setFieldValue('expirationDate', value)}
                                minDate={dayjs()}
                                slotProps={{
                                  textField: {
                                    size: 'small',
                                    fullWidth: false,
                                    name: 'expirationDate',
                                    error: formik.touched.expirationDate && Boolean(formik.errors.expirationDate),
                                    helperText: <ErrorMessage name="expirationDate" />,
                                    onBlur: () => formik.setFieldTouched('expirationDate', true),
                                  },
                                  field: {
                                    onClick: () => !currentItem?.isFilled && setDatePickerOpen(index, true),
                                  }
                                }}
                                open={currentItem?.isFilled ? false : currentItem?.datePickerOpen}
                                onOpen={() => !currentItem.isFilled && setDatePickerOpen(index, true)}
                                onClose={() => setDatePickerOpen(index, false)}
                              />
                            </LocalizationProvider>
                          )
                          : <Field
                            type="number"
                            className="nameParameter"
                            as={TextField}
                            size="small"
                            min={2}
                            max={100}
                            id="trialDays"
                            name="trialDays"
                            autoComplete="off"
                            inputProps={{
                              maxLength: 16,
                            }}
                            onKeyPress={(e: any) => numaricInput(e)}
                            disabled={currentItem.isFilled || isPayGUserRoleSelected(selectedRoles) || selectedRoles.includes(ROLES.SUPER_ADMIN)}
                            error={
                              formik.touched.trialDays &&
                              Boolean(formik.errors.trialDays)
                            }
                            helperText={<ErrorMessage name="trialDays" />}
                          />
                        }
                      </StyledTableCellTrialDays>
                      <StyledTableCell className="tebleCell">
                        <FormControl sx={{ width: '92%', margin: 0 }} error={formik.touched.team && !formik.values.team && (isPayGUserRoleSelected(selectedRoles) || isBasicOrProUser(selectedRoles) || isDataBrowserUser(selectedRoles))}>
                          <Select
                            labelId="team-multiple-checkbox-label"
                            id="team-multiple-checkbox"
                            className="team"
                            name="team"
                            value={(isPayGUserRoleSelected(selectedRoles) || isBasicOrProUser(selectedRoles) || isDataBrowserUser(selectedRoles)) ? formik.values.team : ''}
                            onChange={(event) => {
                              formik.handleChange(event);
                              setTimeout(() => {
                                formik.setFieldTouched('team', false);
                                formik.setErrors((prevErrors: any) => ({
                                  ...prevErrors,
                                  team: '',
                                }));
                              }, 100);
                            }}
                            disabled={currentItem.isFilled || !(isPayGUserRoleSelected(selectedRoles) || isBasicOrProUser(selectedRoles) || isDataBrowserUser(selectedRoles))}
                            onClick={() => {
                              formik.setFieldTouched('team');
                            }}
                          >
                            <ListSubheader>
                              <Box
                                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                                onClick={() => setOpenCreateNewTeam(true)}
                              >
                                <Add /> Create New
                              </Box>
                            </ListSubheader>
                            {teams.length && teams.map((team: { id: string, name: string }) => (
                              <MenuItem key={team.id} value={team.id}>
                                <ListItemText primary={team.name} />
                              </MenuItem>
                            ))}
                          </Select>
                          {formik.touched.team && !formik.values.team && (isPayGUserRoleSelected(selectedRoles) || isBasicOrProUser(selectedRoles) || isDataBrowserUser(selectedRoles)) &&
                            <FormHelperText>
                              {formik.errors.team}
                            </FormHelperText>
                          }
                        </FormControl>
                      </StyledTableCell>
                      <StyledTableCellRole className="tebleCell">
                        <RadioGroup
                          row
                          sx={{ flexWrap: 'nowrap' }}
                          aria-labelledby="radio-buttons-mfa-2fa"
                          name="radio-buttons-mfa-2fa"
                          value={formik.values.isMfaEnabled ? 'MFA' : formik.values.isTwofaEnabled ? '2FA' : ''}
                          onChange={e => {
                            const value = (e.target.value);
                            const is2fa = value === '2FA';
                            const isMfa = value === 'MFA';
                            formik.setFieldValue('isTwofaEnabled', is2fa);
                            setTimeout(() => {
                              formik.setFieldValue('isMfaEnabled', isMfa);
                            }, 100);
                          }}
                        >
                          <FormControlLabel value="MFA" control={<Radio />} label="MFA" disabled={selectedRoles.includes(ROLES.CONTRACTS_TRAIL) || selectedRoles.includes(ROLES.GUEST) || currentItem.isFilled || selectedRoles.includes(ROLES.SSO_USER) || selectedRoles.includes(ROLES.BULKDATA_API_USER)} />
                          <FormControlLabel value="2FA" control={<Radio />} label="2FA" disabled={selectedRoles.includes(ROLES.CONTRACTS_TRAIL) || selectedRoles.includes(ROLES.GUEST) || currentItem.isFilled || selectedRoles.includes(ROLES.SSO_USER) || selectedRoles.includes(ROLES.BULKDATA_API_USER)} />
                        </RadioGroup>
                      </StyledTableCellRole>
                      <StyledTableCellFooterRow className={!(formik.isValid && formik.dirty) ? "tebleCellButton" : "tableInvalid"} >
                        {!currentItem.isFilled ? (
                          <>
                            {!isLoading ? (
                              <Button
                                disabled={!(formik.isValid && formik.dirty) || currentItem.isEmailExist}
                                className="addUserButton"
                                variant="contained"
                                type="submit"
                                sx={{ marginLeft: '5px' }}
                              >
                                <PersonAddIcon />
                              </Button>
                            ) : (
                              <div className="createdUserIcon">
                                <CircularProgress className="circularProgress" size={18} />
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="createdUserIcon" >
                            <img
                              src={require("../../assets/images/accountApprove.png")}
                              alt='accountApprove'
                            />
                          </div>
                        )}
                      </StyledTableCellFooterRow>
                    </Form>
                  </React.Fragment>
                )
              }}
            </Formik>
          </div>
        );
      })}
      {msgConfig.snackFlag && (
        <div>
          <SnackBarMessage snacBarValue={msgConfig} />
        </div>
      )}
      <Modal title={"Create New Team"} open={openCreateNewTeam} onClose={handleCloseCreateNewTeam}>
        <CreateTeam onCancel={handleCloseCreateNewTeam} fetchAndClose={fetchAndClose} />
      </Modal>
    </>
  );
};